import { useState } from 'react';
import {
  Stack, Button, Chip, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  Alert, Snackbar, LinearProgress, Box, Paper, Divider, Table, TableHead, TableBody, TableRow, TableCell, IconButton, Link,
  Stepper, Step, StepButton, StepLabel,
} from '@mui/material';
import { Add, ArrowBack, Close } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { epcSiteMonitoringApi, type SiteMonitoring, type MonitoringItem } from '../../api/epcSiteMonitoring.api';
import { clientsApi } from '../../api/clients.api';
import { projectsApi } from '../../api/projects.api';
import { MultiFileUpload } from '../../components/MultiFileUpload';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const human = (s: string) => s.replace(/_/g, ' ');
const fmtDate = (s?: string | null) => (s ? String(s).slice(0, 10) : '—');
/** Whole days from today to the given date (positive = days left, negative = overdue). */
const balanceDays = (target?: string | null): number | null => {
  if (!target) return null;
  const t = new Date(String(target).slice(0, 10)).getTime();
  const today = new Date(new Date().toISOString().slice(0, 10)).getTime();
  return Number.isNaN(t) ? null : Math.round((t - today) / 86400000);
};
const statusColor: Record<string, 'default' | 'info' | 'success' | 'warning'> = { pending: 'default', in_progress: 'warning', done: 'success', na: 'info' };
const statusLabel: Record<string, string> = { pending: 'Not started', in_progress: 'In process', done: 'Completed', na: 'N/A' };

export function EpcSiteMonitoringPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [openId, setOpenId] = useState<number | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['epc-monitoring', page, limit], queryFn: () => epcSiteMonitoringApi.list({ page, limit }) });
  const canCreate = hasPermission('epc_site_monitoring.create');

  if (openId) return <MonitoringDetail id={openId} onBack={() => setOpenId(null)} onToast={setToast} toast={toast} />;

  const columns: Column<SiteMonitoring>[] = [
    { key: 'code', label: 'Code', width: 140 },
    { key: 'clientName', label: 'Client', render: (r) => r.clientName ?? '—' },
    { key: 'projectName', label: 'Project', render: (r) => r.projectName ?? '—' },
    { key: 'siteName', label: 'Site', render: (r) => r.siteName ?? `#${r.siteId}` },
    { key: 'stateName', label: 'State', render: (r) => r.stateName ?? '—' },
    { key: 'projectManagerName', label: 'Project Mgr', render: (r) => r.projectManagerName ?? '—' },
    { key: 'romName', label: 'ROM', render: (r) => r.romName ?? '—' },
    { key: 'currentStage', label: 'Current Stage', render: (r) => (
      r.currentStage
        ? <Chip size="small" color="warning" variant="outlined" label={`${r.currentStage}${typeof r.currentStagePercent === 'number' ? ` · ${r.currentStagePercent}%` : ''}`} />
        : <Chip size="small" color="success" label="All stages done" />
    ) },
    { key: 'progress', label: 'Progress', render: (r) => (
      <Box sx={{ minWidth: 160 }}>
        <Stack direction="row" justifyContent="space-between"><Typography variant="caption">{r.overallPercent}%</Typography></Stack>
        <LinearProgress variant="determinate" value={r.overallPercent} sx={{ height: 8, borderRadius: 1 }} color={r.overallPercent === 100 ? 'success' : 'primary'} />
      </Box>
    ) },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" color={r.status === 'completed' ? 'success' : r.status === 'on_hold' ? 'warning' : 'info'} label={human(r.status)} /> },
    { key: 'actions', label: '', width: 80, render: (r) => <Button size="small" onClick={() => setOpenId(r.id)}>Open</Button> },
  ];

  return (
    <>
      <PageHeader title="Site Monitoring" subtitle="Track each site's full execution — service type → sub-service type — with live progress."
        action={canCreate ? <Button startIcon={<Add />} variant="contained" onClick={() => setNewOpen(true)}>Start Monitoring</Button> : undefined} />
      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading} page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id} />

      {newOpen && <StartDialog onClose={() => setNewOpen(false)} onDone={(id) => { setNewOpen(false); setToast('Monitoring started'); qc.invalidateQueries({ queryKey: ['epc-monitoring'] }); setOpenId(id); }} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function StartDialog({ onClose, onDone }: { onClose: () => void; onDone: (id: number) => void }) {
  const [clientId, setClientId] = useState<number | ''>('');
  const [projectId, setProjectId] = useState<number | ''>('');
  const [siteId, setSiteId] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);

  const clients = useQuery({ queryKey: ['clients', 'active'], queryFn: () => clientsApi.list({ limit: 500, status: 'active' }) });
  const projects = useQuery({ queryKey: ['projects', 'all'], queryFn: () => projectsApi.list({ limit: 1000 }) });
  // EPC-owned, non-region-scoped site lookup so every project's sites are pickable regardless of the operator's region.
  const sites = useQuery({ queryKey: ['epc-monitoring', 'candidate-sites'], queryFn: () => epcSiteMonitoringApi.candidateSites() });
  const client = (clients.data?.data ?? []).find((c) => c.id === clientId);
  const clientProjects = (projects.data?.data ?? []).filter((p) => p.clientId === clientId);
  const projectSites = (sites.data ?? []).filter((s) => s.projectId === projectId);

  const mut = useMutation({ mutationFn: () => epcSiteMonitoringApi.create({ siteId: Number(siteId) }), onSuccess: (m) => onDone(m.id), onError: (e) => setError(extractApiError(e)) });
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Start Site Monitoring</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField select label="Client *" value={clientId} onChange={(e) => { setClientId(e.target.value ? Number(e.target.value) : ''); setProjectId(''); setSiteId(''); }} fullWidth>
            {(clients.data?.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.clientName}</MenuItem>)}
          </TextField>
          {client && <Typography variant="caption" color="text.secondary">{[client.contactPerson, client.mobile, client.email].filter(Boolean).join(' · ') || 'No contact on file'} · {clientProjects.length} project(s)</Typography>}
          <TextField select label="Project *" value={projectId} disabled={!clientId} onChange={(e) => { setProjectId(e.target.value ? Number(e.target.value) : ''); setSiteId(''); }} fullWidth
            helperText={clientId && clientProjects.length === 0 ? 'No projects for this client' : ''}>
            {clientProjects.map((p) => <MenuItem key={p.id} value={p.id}>{p.projectCode} — {p.projectName}</MenuItem>)}
          </TextField>
          <TextField select label="Site *" value={siteId} disabled={!projectId} onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : '')} fullWidth
            helperText={projectId && projectSites.length === 0 ? 'No sites available for this project (onboard a site, or it may already be under monitoring)' : 'One monitoring record per site — the standard service checklist is added automatically.'}>
            {projectSites.map((s) => <MenuItem key={s.id} value={s.id}>{s.siteName} ({s.siteCode})</MenuItem>)}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!siteId || mut.isPending} onClick={() => mut.mutate()}>Start</Button>
      </DialogActions>
    </Dialog>
  );
}

function MonitoringDetail({ id, onBack, onToast, toast }: { id: number; onBack: () => void; onToast: (s: string) => void; toast: string | null }) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('epc_site_monitoring.update');
  const [activeStep, setActiveStep] = useState(0);
  const { data: m } = useQuery({ queryKey: ['epc-monitoring', id], queryFn: () => epcSiteMonitoringApi.get(id) });
  const updateMut = useMutation({
    mutationFn: (v: { itemId: number; body: { status?: string; percentComplete?: number; remarks?: string; completionDate?: string | null } }) => epcSiteMonitoringApi.updateItem(v.itemId, v.body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['epc-monitoring'] }); onToast('Updated');
      // Auto-advance to the next service type once the current one is 100% complete.
      setActiveStep((prev) => { const svc = data.services?.[prev]; return svc && svc.percent === 100 && prev < (data.services!.length - 1) ? prev + 1 : prev; });
    },
  });
  const attachMut = useMutation({
    mutationFn: async (v: { itemId: number; files: { url: string; name: string }[] }) => {
      for (const f of v.files) await epcSiteMonitoringApi.addAttachment(v.itemId, { fileUrl: f.url, fileName: f.name });
    },
    onSuccess: (_r, v) => { qc.invalidateQueries({ queryKey: ['epc-monitoring'] }); onToast(`${v.files.length} attachment${v.files.length === 1 ? '' : 's'} added`); },
  });
  const removeAttachMut = useMutation({
    mutationFn: (attId: number) => epcSiteMonitoringApi.removeAttachment(attId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['epc-monitoring'] }); onToast('Attachment removed'); },
  });

  if (!m) return null;
  // Group items by service type in list order.
  const groups: { serviceType: string; items: MonitoringItem[] }[] = [];
  for (const it of m.items ?? []) { let g = groups.find((x) => x.serviceType === it.serviceType); if (!g) { g = { serviceType: it.serviceType, items: [] }; groups.push(g); } g.items.push(it); }

  return (
    <>
      <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 1 }}>Back to sites</Button>
      <PageHeader title={`${m.siteName ?? 'Site'} — ${m.code}`} subtitle={`Overall progress ${m.overallPercent}% · ${human(m.status)}`} />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="caption" color="text.secondary">CLIENT</Typography>
            <Typography variant="body2"><strong>{m.clientName ?? '—'}</strong>{m.clientCode ? ` (${m.clientCode})` : ''}</Typography>
            <Typography variant="caption" color="text.secondary">{[m.clientContact, m.clientMobile, m.clientEmail].filter(Boolean).join(' · ') || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">PROJECT</Typography>
            <Typography variant="body2"><strong>{m.projectName ?? '—'}</strong>{m.projectCode ? ` (${m.projectCode})` : ''}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">SITE</Typography>
            <Typography variant="body2"><strong>{m.siteName ?? '—'}</strong>{m.siteCode ? ` (${m.siteCode})` : ''}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{m.siteAddress ?? '—'}</Typography>
            {m.allocationDate && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Allocated: {fmtDate(m.allocationDate)}</Typography>}
            {m.clientCommittedDate && <Typography variant="caption" color="text.secondary">Client committed: {fmtDate(m.clientCommittedDate)}</Typography>}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">VENDOR</Typography>
            <Typography variant="body2"><strong>{m.vendorName ?? '—'}</strong></Typography>
            <Typography variant="caption" color="text.secondary">{[m.vendorMobile, m.vendorEmail].filter(Boolean).join(' · ') || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">TECHNICIAN</Typography>
            <Typography variant="body2"><strong>{m.technicianName ?? '—'}</strong></Typography>
            <Typography variant="caption" color="text.secondary">{m.technicianMobile ?? '—'}</Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ flex: 1 }}><LinearProgress variant="determinate" value={m.overallPercent} sx={{ height: 12, borderRadius: 1 }} color={m.overallPercent === 100 ? 'success' : 'primary'} /></Box>
          <Typography variant="h6">{m.overallPercent}%</Typography>
        </Stack>
        {/* Overall TAT (allocation → client committed) + balance days remaining to the committed date. */}
        {(m.plannedTatDays != null || m.clientCommittedDate) && (() => {
          const bal = balanceDays(m.clientCommittedDate);
          return (
            <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap alignItems="center">
              {m.plannedTatDays != null && <Chip size="small" color="primary" label={`Overall TAT: ${m.plannedTatDays} day${m.plannedTatDays === 1 ? '' : 's'}`} />}
              <Typography variant="caption" color="text.secondary">Allocated {fmtDate(m.allocationDate)} → Committed {fmtDate(m.clientCommittedDate)}</Typography>
              {bal != null && <Chip size="small" color={bal < 0 ? 'error' : 'success'} label={bal < 0 ? `Overdue by ${Math.abs(bal)} day${Math.abs(bal) === 1 ? '' : 's'}` : `Balance: ${bal} day${bal === 1 ? '' : 's'}`} />}
            </Stack>
          );
        })()}
        <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
          {(m.services ?? []).map((s) => <Chip key={s.serviceType} size="small" variant="outlined" label={`${s.serviceType}: ${s.percent}% (${s.done}/${s.total})`} color={s.percent === 100 ? 'success' : 'default'} />)}
        </Stack>
      </Paper>

      <Stepper activeStep={activeStep} nonLinear alternativeLabel sx={{ mb: 3 }}>
        {groups.map((g, i) => {
          const svc = (m.services ?? []).find((s) => s.serviceType === g.serviceType);
          const done = svc?.percent === 100;
          return (
            <Step key={g.serviceType} completed={done}>
              <StepButton onClick={() => setActiveStep(i)}>
                <StepLabel optional={<Typography variant="caption" color="text.secondary">{svc?.percent ?? 0}% ({svc?.done ?? 0}/{svc?.total ?? g.items.length})</Typography>}>{g.serviceType}</StepLabel>
              </StepButton>
            </Step>
          );
        })}
      </Stepper>

      {groups.slice(activeStep, activeStep + 1).map((g) => (
        <Box key={g.serviceType} sx={{ mb: 2 }}>
          <Divider textAlign="left" sx={{ mb: 1 }}><Typography variant="subtitle2">{g.serviceType}</Typography></Divider>
          <Table size="small">
            <TableHead><TableRow><TableCell>Sub-service</TableCell><TableCell width={150}>Status</TableCell><TableCell width={160}>Completion Date</TableCell><TableCell width={90}>TAT</TableCell><TableCell>Remarks</TableCell><TableCell width={220}>Supporting attachments</TableCell></TableRow></TableHead>
            <TableBody>
              {g.items.map((it) => (
                <TableRow key={it.id}>
                  <TableCell>{it.subServiceType}</TableCell>
                  <TableCell>
                    {it.status === 'done' ? (
                      <Chip size="small" color="success" label="Completed" />
                    ) : canEdit ? (
                      <TextField select size="small" variant="standard" value={it.status === 'na' ? 'pending' : it.status} onChange={(e) => updateMut.mutate({ itemId: it.id, body: { status: e.target.value } })} sx={{ minWidth: 140 }}>
                        {['pending', 'in_progress'].map((s) => <MenuItem key={s} value={s}>{statusLabel[s]}</MenuItem>)}
                      </TextField>
                    ) : <Chip size="small" color={statusColor[it.status]} label={statusLabel[it.status] ?? human(it.status)} />}
                  </TableCell>
                  <TableCell>
                    {canEdit && it.status !== 'done' ? (
                      <TextField type="date" size="small" variant="standard" value={it.completionDate ?? ''} InputLabelProps={{ shrink: true }}
                        onChange={(e) => updateMut.mutate({ itemId: it.id, body: { completionDate: e.target.value || null } })}
                        sx={{ width: 150 }} />
                    ) : (it.completionDate ? fmtDate(it.completionDate) : '—')}
                  </TableCell>
                  <TableCell>
                    {typeof it.tatDays === 'number' ? <Chip size="small" variant="outlined" label={`${it.tatDays} day${it.tatDays === 1 ? '' : 's'}`} /> : '—'}
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <TextField size="small" variant="standard" fullWidth defaultValue={it.remarks ?? ''}
                        onBlur={(e) => { if (e.target.value !== (it.remarks ?? '')) updateMut.mutate({ itemId: it.id, body: { remarks: e.target.value } }); }} placeholder="—" />
                    ) : (it.remarks ?? '—')}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      {(it.attachments ?? []).map((a) => (
                        <Stack key={a.id} direction="row" spacing={0.5} alignItems="center">
                          <Link href={a.fileUrl} target="_blank" rel="noreferrer" variant="caption" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.fileName || 'file'}</Link>
                          {canEdit && <IconButton size="small" color="error" onClick={() => removeAttachMut.mutate(a.id)}><Close sx={{ fontSize: 14 }} /></IconButton>}
                        </Stack>
                      ))}
                      {canEdit && it.status !== 'done' && <MultiFileUpload label="Add proof" accept=".pdf,image/*,.doc,.docx,.xls,.xlsx" onAdd={(files) => attachMut.mutate({ itemId: it.id, files })} />}
                      {it.status === 'done' ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="small" color="success" label="Completed" />
                          {canEdit && <Button size="small" color="inherit" onClick={() => updateMut.mutate({ itemId: it.id, body: { status: 'pending' } })}>Reopen</Button>}
                        </Stack>
                      ) : (
                        canEdit && (() => {
                          const hasDate = Boolean(it.completionDate);
                          const hasDoc = (it.attachments?.length ?? 0) > 0;
                          const ready = hasDate && hasDoc;
                          return (
                            <>
                              <Button size="small" variant="contained" color="success" disabled={updateMut.isPending || !ready}
                                onClick={() => updateMut.mutate({ itemId: it.id, body: { status: 'done' } })}>Submit &amp; complete</Button>
                              {!ready && <Typography variant="caption" color="text.secondary">{!hasDate && !hasDoc ? 'Set completion date & upload a document' : !hasDate ? 'Set completion date' : 'Upload a document'} to submit</Typography>}
                            </>
                          );
                        })()
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
        <Button disabled={activeStep === 0} startIcon={<ArrowBack />} onClick={() => setActiveStep((s) => Math.max(0, s - 1))}>Previous</Button>
        {activeStep < groups.length - 1
          ? <Button variant="contained" onClick={() => setActiveStep((s) => Math.min(groups.length - 1, s + 1))}>Next service</Button>
          : <Chip color={m.overallPercent === 100 ? 'success' : 'default'} label={m.overallPercent === 100 ? 'All services complete' : 'Last service'} />}
      </Stack>
      <Snackbar open={Boolean(toast)} autoHideDuration={3000} onClose={() => onToast('')}><Alert severity="success">{toast}</Alert></Snackbar>
    </>
  );
}
